import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar                from "./components/Sidebar";
import OrdersSection          from "./sections/OrdersSection";
import ProductsSection        from "./sections/ProductsSection";
import CollectionsSection     from "./sections/CollectionsSection";
import BrandsSection          from "./sections/BrandsSection";
import AdminsSection          from "./sections/AdminsSection";
import ReferralsSection       from "./sections/ReferralsSection";
import CustomersSection       from "./sections/CustomersSection";
import DeliverySection        from "./sections/DeliverySection";
import EventsSection          from "./sections/EventsSection";
import WithdrawalsSection     from "./sections/WithdrawalsSection";
import StoreSettings          from "./sections/StoreSettings";
import NotificationsSection   from "./sections/NotificationsSection";
import { useAuth }            from "../context/AuthContext";
import { db }                 from "../firebase/config";
import "../styles/dashboard.css";

const LAST_SEEN_KEY = "gvs_notif_last_seen";

const SECTIONS = {
  notifications: <NotificationsSection />,
  orders:        <OrdersSection />,
  products:      <ProductsSection />,
  collections:   <CollectionsSection />,
  brands:        <BrandsSection />,
  admins:        <AdminsSection />,
  referrals:     <ReferralsSection />,
  customers:     <CustomersSection />,
  delivery:      <DeliverySection />,
  events:        <EventsSection />,
  withdrawals:   <WithdrawalsSection />,
  settings:      <StoreSettings />,
};

export default function Dashboard() {
  const [activeSection, setActiveSection] = useState("orders");
  const [notifCount,    setNotifCount]    = useState(0);
  const [drawerOpen,    setDrawerOpen]    = useState(false);
  const { isAdmin, loading } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  // Open the correct tab when navigated here with ?tab=
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get("tab");
    if (tab && SECTIONS[tab]) setActiveSection(tab);
  }, [location.search]);

  useEffect(() => {
    if (!loading && !isAdmin) navigate("/");
  }, [loading, isAdmin, navigate]);

  // Live notification badge — counts orders + sign-ups since last seen
  useEffect(() => {
    if (!isAdmin) return;

    const lastSeen    = localStorage.getItem(LAST_SEEN_KEY);
    const lastSeenDate = lastSeen ? new Date(lastSeen) : new Date(0);

    const isNew = (doc) => {
      const ts = doc.createdAt;
      if (!ts) return false;
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d > lastSeenDate;
    };

    let orderNew      = 0;
    let userNew       = 0;
    let withdrawalNew = 0;

    const recompute = () => setNotifCount(orderNew + userNew + withdrawalNew);

    const unsubOrders = db.collection("orders")
      .orderBy("createdAt", "desc")
      .limit(50)
      .onSnapshot((snap) => {
        orderNew = snap.docs.filter((d) => isNew(d.data())).length;
        recompute();
      });

    const unsubUsers = db.collection("users")
      .orderBy("createdAt", "desc")
      .limit(50)
      .onSnapshot((snap) => {
        userNew = snap.docs.filter((d) => isNew(d.data())).length;
        recompute();
      });

    const unsubWithdrawals = db.collection("referral_withdrawals")
      .orderBy("createdAt", "desc")
      .limit(50)
      .onSnapshot((snap) => {
        withdrawalNew = snap.docs.filter((d) => isNew(d.data())).length;
        recompute();
      });

    return () => { unsubOrders(); unsubUsers(); unsubWithdrawals(); };
  }, [isAdmin]);

  // Clear badge when notifications tab is opened
  useEffect(() => {
    if (activeSection === "notifications") {
      localStorage.setItem(LAST_SEEN_KEY, new Date().toISOString());
      setNotifCount(0);
    }
  }, [activeSection]);

  if (loading || !isAdmin) return null;

  const handleNavSelect = (key) => {
    setActiveSection(key);
    setDrawerOpen(false);
  };

  return (
    <div className="dashboard">
      <Sidebar
        activeSection={activeSection}
        setActiveSection={handleNavSelect}
        notifCount={notifCount}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
      />
      <main className="dash-main">
        <div className="dash-topbar">
          <button className="dash-topbar__hamburger" onClick={() => setDrawerOpen(true)}>
            <span /><span /><span />
          </button>
          <button className="dash-topbar__store-btn" onClick={() => navigate("/")}>
            ← View Store
          </button>
        </div>
        {SECTIONS[activeSection]}
      </main>
    </div>
  );
}
